/**
 * Light, dark or system color mode.
 *
 * The choice is stored in localStorage under THEME_MODE_STORAGE_KEY. The
 * resolved mode is the `dark` class on <html>, which the dark values in
 * src/assets/styles/index.css key on. index.html runs the same logic in an
 * inline script before the first paint, so the page does not flash light.
 * Keep the two in step.
 */

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedThemeMode = "light" | "dark";

export const THEME_MODE_STORAGE_KEY = "finishd-theme-mode";

/**
 * The mode when nothing is stored. A product decision: follow the OS setting,
 * and a user chooses Light or Dark in Settings. index.html repeats it.
 */
export const DEFAULT_THEME_MODE: ThemeMode = "system";

const DARK_QUERY = "(prefers-color-scheme: dark)";
const MODES: ThemeMode[] = ["light", "dark", "system"];

function isThemeMode(value: unknown): value is ThemeMode {
	return typeof value === "string" && MODES.includes(value as ThemeMode);
}

/** The stored choice, or DEFAULT_THEME_MODE when nothing valid is stored. */
export function readStoredThemeMode(): ThemeMode {
	try {
		const stored = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
		return isThemeMode(stored) ? stored : DEFAULT_THEME_MODE;
	} catch {
		return DEFAULT_THEME_MODE;
	}
}

function systemPrefersDark(): boolean {
	return (
		typeof window.matchMedia === "function" &&
		window.matchMedia(DARK_QUERY).matches
	);
}

export function resolveThemeMode(mode: ThemeMode): ResolvedThemeMode {
	if (mode === "system") return systemPrefersDark() ? "dark" : "light";
	return mode;
}

function applyResolvedMode(resolved: ResolvedThemeMode): void {
	const root = document.documentElement;
	root.classList.toggle("dark", resolved === "dark");
	root.style.colorScheme = resolved;
}

interface ThemeModeSnapshot {
	mode: ThemeMode;
	resolved: ResolvedThemeMode;
}

let snapshot: ThemeModeSnapshot = {
	mode: DEFAULT_THEME_MODE,
	resolved: "light",
};
const listeners = new Set<() => void>();

function publish(mode: ThemeMode): void {
	const resolved = resolveThemeMode(mode);
	applyResolvedMode(resolved);
	if (snapshot.mode !== mode || snapshot.resolved !== resolved) {
		snapshot = { mode, resolved };
		for (const listener of listeners) listener();
	}
}

let systemListenerAdded = false;

/**
 * Reads the stored choice, applies it, and follows the OS setting while the
 * choice is "system". Call once at startup, before the first render.
 */
export function initThemeMode(): void {
	publish(readStoredThemeMode());
	if (!systemListenerAdded) {
		if (typeof window.matchMedia === "function") {
			window.matchMedia(DARK_QUERY).addEventListener("change", () => {
				if (snapshot.mode === "system") publish("system");
			});
		}
		// A choice made in another tab arrives as a storage event.
		window.addEventListener("storage", (event) => {
			if (event.key === THEME_MODE_STORAGE_KEY) publish(readStoredThemeMode());
		});
		systemListenerAdded = true;
	}
}

export function setThemeMode(mode: ThemeMode): void {
	try {
		window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
	} catch {
		// Storage can be unavailable (private mode). The choice still applies
		// for this page load.
	}
	publish(mode);
}

/**
 * Shows a mode without storing it or changing the snapshot. The styleguide
 * uses it to compare the palettes. The next publish (a choice, an OS change
 * in "system" mode, or another tab) replaces the preview.
 */
export function previewThemeMode(resolved: ResolvedThemeMode): void {
	applyResolvedMode(resolved);
}

/** Puts back the mode that the stored choice resolves to. */
export function endThemeModePreview(): void {
	applyResolvedMode(snapshot.resolved);
}

export function getThemeModeSnapshot(): ThemeModeSnapshot {
	return snapshot;
}

export function subscribeThemeMode(listener: () => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}
